import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { FileSpreadsheet, ChevronRight, PlusCircle } from "lucide-react";

const STATUS_STYLES = {
  submitted: "bg-blue-100 text-blue-800",
  quoted:    "bg-emerald-100 text-emerald-800",
  closed:    "bg-slate-100 text-slate-700",
  cancelled: "bg-rose-100 text-rose-800",
};

export default function RfqHistory() {
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/school-rfqs");
      setRfqs(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <DashboardLayout nav={SCHOOL_NAV} title="My RFQs">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">{rfqs.length} request(s) in your history</div>
        <Link to="/school/order" data-testid="btn-new-rfq"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blueDark">
          <PlusCircle size={14} /> New RFQ
        </Link>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : rfqs.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="rfq-empty">
          <FileSpreadsheet size={36} className="mx-auto text-slate-300 mb-3" />
          You haven't submitted any RFQs yet.
          <div className="mt-4">
            <Link to="/school/order" className="text-brand-blue font-semibold">Request your first quotation →</Link>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">RFQ #</th>
                <th className="text-left px-4 py-3">Submitted</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-left px-4 py-3">Required By</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rfqs.map((r) => (
                <tr key={r.id} data-testid={`rfq-row-${r.id}`} className="hover:bg-slate-50/50 cursor-pointer" onClick={() => setActive(r)}>
                  <td className="px-4 py-3 font-mono text-brand-navy font-semibold">{r.rfq_number}</td>
                  <td className="px-4 py-3 text-slate-600">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.items || []).length}</td>
                  <td className="px-4 py-3 text-slate-600">{r.delivery_required_by || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right"><ChevronRight size={14} className="text-slate-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {active && <RfqDetail rfq={active} close={() => setActive(null)} />}
    </DashboardLayout>
  );
}

function RfqDetail({ rfq, close }) {
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="rfq-detail">
      <div className="flex-1 bg-black/40" onClick={close} />
      <aside className="w-full sm:w-[480px] h-full bg-white flex flex-col overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <div className="text-xs text-slate-500">RFQ Number</div>
            <div className="font-heading font-semibold text-brand-navy">{rfq.rfq_number}</div>
          </div>
          <button onClick={close} className="text-slate-400 hover:text-slate-800">✕</button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Status" value={rfq.status} />
            <Info label="Submitted" value={rfq.submitted_at ? new Date(rfq.submitted_at).toLocaleString() : "—"} />
            <Info label="Required by" value={rfq.delivery_required_by || "—"} />
            <Info label="Contact email" value={rfq.contact_email || "—"} />
          </div>
          {rfq.notes && (
            <div>
              <div className="text-xs font-semibold text-slate-500">Notes</div>
              <div className="text-slate-800 whitespace-pre-line">{rfq.notes}</div>
            </div>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-500 mb-2">Items requested</div>
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
              {(rfq.items || []).map((it, idx) => (
                <div key={idx} className="p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-slate-50 grid place-items-center overflow-hidden flex-shrink-0">
                    {it.product_image ? <img src={it.product_image} alt="" className="max-w-full max-h-full object-contain" onError={(e)=>{e.currentTarget.src="/api/static/library-covers/cover-unavailable.webp";}}/>
                                       : <span className="text-slate-400 text-[10px]">n/a</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 text-sm truncate">{it.product_name}</div>
                    <div className="text-xs text-slate-500">{it.product_category}</div>
                    {it.notes && <div className="text-xs text-slate-500 italic mt-0.5">{it.notes}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-slate-500">Qty</div>
                    <div className="font-heading font-bold text-brand-navy">{it.qty}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {rfq.status === "quoted" && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-emerald-900 text-sm">
              A formal quotation has been shared with your school. Please check your registered email for details.
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="text-slate-800 capitalize">{value}</div>
    </div>
  );
}
